import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Robust SPA hash-scroller.
 *
 * Feb-2026 fix for the "How It Works" nav link (and any other hash
 * anchor).  Handles four flows:
 *
 *   1. On-homepage click on a hash link (`<Link to="/#how-it-works">`).
 *      The Header intercepts the click, calls `scrollAndFocus`, and
 *      does `history.replaceState` — the same scroll-plus-settle path
 *      as the router-driven case.
 *
 *   2. Cross-route click (`/cart` → click "How It Works").  Router
 *      unmounts the current route, mounts Home, then `useLocation`
 *      re-fires with the new pathname + hash.  We poll a bounded
 *      requestAnimationFrame queue (max ~60 frames / ~1 s) until the
 *      target element mounts, then scroll.
 *
 *   3. Direct load of `/#how-it-works` — first `useLocation` value
 *      already carries the hash; identical polling loop applies.
 *
 *   4. Browser Back / Forward / `hashchange`.  React Router listens
 *      to `popstate`, so `useLocation` re-fires and the same routine
 *      runs.
 *
 * Behaviour:
 *   * Respects `prefers-reduced-motion: reduce` (uses `behavior: "auto"`).
 *   * Uses the target's CSS `scroll-margin-top` to clear the sticky
 *     announcement-bar + header stack — no JS offset math that would
 *     drift when the header height changes.
 *   * After the initial scroll, runs a bounded settle-pass sequence
 *     (setTimeout at 450 / 950 / 1500 / 2400 / 3500 ms) that re-checks
 *     the target position and re-aligns instantly when late-loading
 *     images push the anchor further down the page.  All timers are
 *     stored in a caller-provided ref so they can be cancelled on
 *     unmount / next location change.
 *   * After scroll, moves keyboard focus to the target via
 *     `focus({ preventScroll: true })` so screen readers announce the
 *     section without a second scroll and without an intrusive pointer
 *     outline (`:focus-visible` styling only).
 *   * Bounded polling — clears its rAF + timer handles on unmount, on
 *     the next location change, and once the target is found.  No
 *     timer leaks, no infinite loops.
 */

const MAX_FRAMES = 60;      // ~1 s at 60 fps — bounded, no polling storm.
// Cross-route hops trigger image-load-driven height changes below the
// fold.  A single scrollIntoView call locks onto the target's position
// at call time, so late-loading images push the anchor further down.
// We re-align at these delays (ms), capped and cleared on unmount.
const SETTLE_DELAYS_MS = [450, 950, 1500, 2400, 3500];

function reducedMotionMatches() {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  } catch (_e) {
    return false;
  }
}

/**
 * Scroll + focus a mounted element AND schedule up to 5 bounded settle
 * passes to re-align if the layout shifts (e.g. below-the-fold images
 * finishing their load).  Returns a cleanup function that clears every
 * scheduled timer.  Idempotent; safe to call twice.
 */
export function scrollAndFocus(el, { reduced } = {}) {
  if (!el || typeof el.scrollIntoView !== "function") return () => {};
  const reducedResolved = reduced ?? reducedMotionMatches();
  const behavior = reducedResolved ? "auto" : "smooth";
  el.scrollIntoView({ behavior, block: "start", inline: "nearest" });
  // Move keyboard focus without triggering a second scroll — respects
  // both `:focus-visible` accessibility and pointer-user calm.
  if (typeof el.focus === "function") {
    try {
      el.focus({ preventScroll: true });
    } catch (_e) {
      /* older browsers: fall through silently */
    }
  }

  const timers = [];
  for (const delay of SETTLE_DELAYS_MS) {
    const t = setTimeout(() => {
      if (!document.body.contains(el)) return;
      const rect = el.getBoundingClientRect();
      const cssMt =
        parseInt(window.getComputedStyle(el).scrollMarginTop, 10) || 0;
      // "In position" = the top of the element is within ~120 px of
      // the CSS scroll-margin-top slot beneath the sticky header.
      if (Math.abs(rect.top - cssMt) > 120) {
        el.scrollIntoView({ behavior: "auto", block: "start" });
      }
    }, delay);
    timers.push(t);
  }
  return () => timers.forEach(clearTimeout);
}

/**
 * <HashScroller /> — mount once inside <BrowserRouter>.  Renders nothing.
 * Watches `useLocation()` and drives the scroll routine above.
 */
export function HashScroller() {
  const { hash, key } = useLocation();
  const rafRef = useRef(0);
  const framesRef = useRef(0);
  const settleTimersRef = useRef([]);
  const cancelSettleRef = useRef(null);

  useEffect(() => {
    // Always start clean.
    cancelAnimationFrame(rafRef.current);
    settleTimersRef.current.forEach(clearTimeout);
    settleTimersRef.current = [];
    if (typeof cancelSettleRef.current === "function") {
      cancelSettleRef.current();
      cancelSettleRef.current = null;
    }
    framesRef.current = 0;

    if (!hash || hash.length < 2) return undefined;
    const id = decodeURIComponent(hash.slice(1));
    if (!id) return undefined;

    const reduced = reducedMotionMatches();

    const tick = () => {
      framesRef.current += 1;
      const el = document.getElementById(id);
      if (el) {
        // `scrollAndFocus` returns a cleanup that clears its bounded
        // settle timers if we unmount / change hash mid-flight.
        cancelSettleRef.current = scrollAndFocus(el, { reduced });
        return; // done — no next frame scheduled.
      }
      if (framesRef.current >= MAX_FRAMES) {
        return; // give up cleanly, no timer leak.
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      framesRef.current = 0;
      settleTimersRef.current.forEach(clearTimeout);
      settleTimersRef.current = [];
      if (typeof cancelSettleRef.current === "function") {
        cancelSettleRef.current();
        cancelSettleRef.current = null;
      }
    };
    // `key` changes on every history push — this is what makes a
    // click on the same `/#how-it-works` link scroll again even when
    // pathname + hash are unchanged.
  }, [hash, key]);

  return null;
}
